import {
  Component,
  DestroyRef,
  Input,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ExternalService } from 'src/app/core/models/core.models';
import { map, take, tap } from 'rxjs';

import { AppState } from '../../../reducers';
import { ExternalAuthService } from '@services/external-auth.service';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ToastService } from '@services/toast.service';
import { AuthFlowService } from '../../../core/services/auth-flow.service';
import { selectUser } from 'src/app/core/reducers';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-external-service-auth',
  templateUrl: './external-service-auth.component.html',
  styleUrl: './external-service-auth.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ExternalServiceAuthComponent {
  @Input() externalService: ExternalService;

  protected destroyRef = inject(DestroyRef);

  protected router = inject(Router);

  constructor(
    private store: Store<AppState>,
    private toastService: ToastService,
    private externalAuthService: ExternalAuthService,
    private authFlow: AuthFlowService,
  ) {}

  ngOnInit(): void {
    // Define a custom callback for this external service.
    const authResponseCallback = (responseData: any) => {
      // the account is linked and we have received a token to login the user
      if (responseData?.token) {
        this.authFlow.remoteLogin(responseData.token);
      } else {
        const facebookUserId = responseData?.facebook_user_id;
        const facebookAccessToken = responseData?.facebook_access_token;

        if (facebookUserId && facebookAccessToken) {
          // navigate to login with parameters
          const facebookAuthUrl = `/auth/login?facebook_user_id=${facebookUserId}&facebook_access_token=${facebookAccessToken}`;
          this.router.navigateByUrl(facebookAuthUrl);
          return;
        } else {
          this.toastService.showError('Invalid credentials');
        }
      }
    };

    // Register the custom callback with the service.
    this.externalAuthService.registerCallback(
      this.externalService,
      authResponseCallback,
    );
  }

  auth(): void {
    this.store
      .select(selectUser)
      .pipe(
        // pluck out just the id
        map((user) => user?.id),
        // we only need the first valid value, then auto‑unsubscribe
        take(1),
        // side‑effect: open the popup
        tap((userId) =>
          this.externalAuthService.authenticate(this.externalService, userId),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
